import { useState } from "react";
import type { Chair, SlotOffer, WaitlistEntry } from "@dentacore/shared-types";

import { readSlotOffers } from "../api/home";
import { bookFromWaitlist } from "../api/waitlist";
import { clockOf, today } from "./home-data";
import styles from "./home.module.css";
import { Pager, fitArea, useRowPages } from "../ui/paging";

/**
 * Who is waiting for an earlier slot, each with **Fit in** — the gaps today
 * that fit them, and one click to book one.
 *
 * Moved unchanged out of the *Remaining procedures* column into Home's *Needs
 * you* tab (decision CS2): a waiting patient is a thing to act on, and the
 * left column is today's list. It draws nothing when nobody is waiting; the
 * tab says so in one line instead.
 */
export function WaitlistOffers({
  waitlist,
  chairs,
  timezone,
  onChanged,
  onError,
}: {
  waitlist: readonly WaitlistEntry[];
  chairs: readonly Chair[];
  timezone: string;
  onChanged: () => void;
  onError: (message: string) => void;
}): React.JSX.Element | null {
  const [busyId, setBusyId] = useState<string | null>(null);

  /**
   * Which waiting patient's offers are open, and what they are.
   *
   * **Fetched when the row is opened, not with the strip.** A dashboard that
   * asked for every waiting patient's gaps on load would do a chair-by-chair
   * search per entry before anybody had asked for one — and the answer goes
   * stale the moment somebody books, which is exactly what this screen is for.
   */
  const [openFor, setOpenFor] = useState<string | null>(null);
  const [offers, setOffers] = useState<readonly SlotOffer[] | null>(null);

  const chairNameOf = (chairId: string): string =>
    chairs.find((chair) => chair.id === chairId)?.name ?? "—";

  async function offer(entryId: string): Promise<void> {
    if (openFor === entryId) {
      setOpenFor(null);
      return;
    }

    setOpenFor(entryId);
    setOffers(null);

    try {
      setOffers(await readSlotOffers(entryId, today(timezone)));
    } catch (cause) {
      setOffers([]);
      onError(cause instanceof Error ? cause.message : "Could not look for a gap.");
    }
  }

  /**
   * Take one of the offered times.
   *
   * **The server decides, not this.** The offer came from `legalStarts`, which
   * is the conflict service's own rule, so an accepted offer should book — but
   * somebody else may have taken the chair in the seconds since, and that comes
   * back as a refusal with a sentence the front desk can act on.
   */
  async function take(entry: WaitlistEntry, slot: SlotOffer): Promise<void> {
    setBusyId(entry.id);

    try {
      await bookFromWaitlist(entry.id, {
        chairId: slot.chairId,
        startsAt: slot.startsAt,
        ...(entry.procedureLabel === null ? {} : { procedureSummary: entry.procedureLabel }),
      });
      setOpenFor(null);
      setOffers(null);
      onChanged();
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : "That slot could not be booked.");
    } finally {
      setBusyId(null);
    }
  }

  // Paged to the room left in the tab, never scrolled; an opened row is measured
  // as the first one, so the page shortens around its offers.
  const paging = useRowPages({ count: waitlist.length, estimate: 46 });

  if (waitlist.length === 0) return null;

  return (
    <section className={`${styles.need} ${styles.needFill}`} data-testid="home-waitlist">
      <div className={styles.needHead}>
        <span className={styles.needDot} data-tone="warm" aria-hidden="true" />
        <span className={styles.needTitle}>
          {waitlist.length === 1
            ? "1 on the waitlist"
            : `${String(waitlist.length)} on the waitlist`}
        </span>
        <span className={styles.needNote}>could fill a cancellation</span>
      </div>

      <div className={`${fitArea} ${styles.fillArea}`} ref={paging.areaRef}>
        <ul className={styles.rows}>
          {waitlist.slice(paging.start, paging.end).map((entry, index) => (
            <li
              key={entry.id}
              ref={index === 0 ? paging.rowRef : undefined}
              data-testid="waitlist-row"
            >
              <button
                type="button"
                className={styles.waitRow}
                aria-expanded={openFor === entry.id}
                onClick={() => void offer(entry.id)}
                data-card=""
                data-testid="waitlist-offer"
              >
                <span className={styles.patient}>{entry.patientName}</span>
                <span className={styles.procedure}>
                  {entry.procedureLabel ?? "No procedure noted"} &middot; {entry.durationMinutes}{" "}
                  min
                  {entry.preferredWindow !== null && ` · ${entry.preferredWindow}`}
                </span>
                <span className={styles.waitCue} aria-hidden="true">
                  {openFor === entry.id ? "×" : "Fit in"}
                </span>
              </button>

              {openFor === entry.id && (
                <div className={styles.offers} data-testid="waitlist-offers">
                  {offers === null ? (
                    <span className={styles.quiet}>Looking for a gap&hellip;</span>
                  ) : offers.length === 0 ? (
                    <span className={styles.quiet} data-testid="waitlist-no-offers">
                      Nothing free today long enough for {entry.durationMinutes} minutes.
                    </span>
                  ) : (
                    offers.map((slot) => (
                      <button
                        key={`${slot.chairId}-${slot.startsAt}`}
                        type="button"
                        className={styles.offer}
                        disabled={busyId === entry.id}
                        onClick={() => void take(entry, slot)}
                        data-testid="waitlist-take"
                      >
                        {clockOf(slot.startsAt, timezone)}
                        <span className={styles.offerChair}>{chairNameOf(slot.chairId)}</span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
      <Pager
        compact
        page={paging.page}
        count={paging.pages}
        onPage={paging.setPage}
        from={paging.start + 1}
        to={paging.end}
        total={waitlist.length}
        noun="on the waitlist"
        testId="waitlist-pager"
      />
    </section>
  );
}
